import { useMemo, useState } from "react";
import { Copy, Trash2, EyeOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { findDupeGroups, fmtShortDate, CAT_LABEL } from "./budgetUtils";

export const DupeDetector = ({ tripId, extras, expenses, onRefresh }) => {
  const [dismissed, setDismissed] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`vr-dupes-${tripId}`) || "[]"); } catch { return []; }
  });
  const [busy, setBusy] = useState(null);
  const groups = useMemo(() => findDupeGroups(extras), [extras]);
  const keyOf = (g) => g.map((e) => e.id).sort().join("|");
  const visible = groups.filter((g) => !dismissed.includes(keyOf(g)));
  if (!visible.length) return null;

  const dismiss = (g) => {
    const d = [...dismissed, keyOf(g)];
    setDismissed(d);
    localStorage.setItem(`vr-dupes-${tripId}`, JSON.stringify(d));
  };

  const del = async (id) => {
    setBusy(id);
    try {
      await api.delete(`/trips/${tripId}/extra-costs/${id}`);
      toast.success("Dubbele uitgave verwijderd — totalen en saldo's zijn bijgewerkt");
      window.dispatchEvent(new Event("vr-split-refresh"));
      onRefresh?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  const payerOf = (e) => (expenses || []).find((x) => x.id === e.split_expense_id)?.paid_by;

  return (
    <div data-testid="dupe-detector" className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
      <p className="text-sm font-heading font-bold text-[#946200] flex items-center gap-1.5">
        <Copy className="w-4 h-4 shrink-0" /> Mogelijke dubbele uitgaven ({visible.length})
      </p>
      <p className="text-[11px] text-[#946200]/80 mt-0.5 mb-2">Zelfde bedrag en gelijkaardige omschrijving — mogelijk dubbel ingevoerd (bv. handmatig én via import). Verwijder de overbodige, of markeer als "geen dubbel".</p>
      <div className="space-y-2">
        {visible.map((g, gi) => (
          <div key={keyOf(g)} data-testid={`dupe-group-${gi}`} className="bg-card/80 rounded-xl p-3">
            <div className="space-y-1.5">
              {g.map((e) => (
                <div key={e.id} className="flex items-center justify-between gap-2 text-xs">
                  <span className="min-w-0 truncate">
                    <span className="font-semibold">{e.label}</span>
                    <span className="text-muted-foreground"> · {e.date ? fmtShortDate(e.date) : e.day_number != null ? `dag ${e.day_number}` : "geen dag"}{e.category ? ` · ${CAT_LABEL[e.category] || e.category}` : ""}{payerOf(e) ? ` · betaald door ${payerOf(e)}` : ""}</span>
                  </span>
                  <span className="flex items-center gap-2 shrink-0">
                    <span className="font-mono font-bold">€ {e.amount_eur.toFixed(2)}</span>
                    <button type="button" data-testid={`dupe-del-${e.id}`} onClick={() => del(e.id)} disabled={busy != null}
                      title="Verwijder deze uitgave (dubbel)"
                      className="flex items-center gap-1 rounded-full bg-red-50 hover:bg-red-100 text-red-600 font-semibold px-2.5 py-1 transition-[background-color] duration-150 disabled:opacity-50">
                      {busy === e.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />} Verwijder
                    </button>
                  </span>
                </div>
              ))}
            </div>
            <button type="button" data-testid={`dupe-dismiss-${gi}`} onClick={() => dismiss(g)}
              className="mt-2 flex items-center gap-1 text-[11px] font-semibold text-muted-foreground hover:text-foreground transition-[color] duration-150">
              <EyeOff className="w-3 h-3" /> Geen dubbel — niet meer tonen
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};
